import Page from "components/Page";
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import styles from "./../../../ManageSystemInstructions/SystemInstruction.module.scss";
import chatStyles from "../chatHistory.module.scss";
import { withTranslation } from "react-i18next";
import constants from "../../../../../../constants";
import Card from "components/Card";
import Text from "components/Text";
import moment from "moment";
import Button from "components/Button";
import {
  getSessionChat,
  seenChatHistory,
  exportChat,
} from "repositories/ai-assistant-repository";
import { formatLocalDateTime, handleError, handleSuccess } from "utils";
import Loader from "components/Loader";
import CustomFeedbackModal from "components/CustomFeedbackModal";
import Empty from "components/Empty";
import starMarkedIcon from "../../../../../../assets/images/star-rating-marked.svg";
import ImageViewer from "Messenger/pages/TeamConversation/components/Modals/ImageViewer";

function ViewChatDetails({ t, location, history }) {
  const session = location?.state || {};
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const lastScrollHeightRef = useRef(0);
  const didInitialAutoScrollRef = useRef(false);
  const [canHandleScroll, setCanHandleScroll] = useState(false);
  const [topSpacerHeight, setTopSpacerHeight] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [fileViewer, setFileViewer] = useState(false);
  const [fileMessage, setFileMessage] = useState(null);

  const goBack = () =>
    history.push({ pathname: constants.routes.aiAssistant.chatHistory });

  const handleExport = async (session) => {
    try {
      setShowLoader(true);
      const params = {
        AiChatSessionId: session?.id,
        AiAssistantMessageId: null,
        ExportWholeChat: true,
      };
      const resp = await exportChat(params);

      // Handle file download if base64Content is provided
      if (resp?.data?.base64Content && resp?.data?.fileName) {
        const { base64Content, fileName, contentType } = resp.data;

        // Convert base64 to blob
        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        handleSuccess(t("aiAssistant.fileDownloadedSuccessfully"));
      } else {
        handleSuccess(resp?.message || t("aiAssistant.exportCompleted"));
      }
    } catch (error) {
      handleError(error?.message || "");
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    if (session?.isSeenBySuperAdmin) return;
    seenChatDetail();
  }, []);

  const seenChatDetail = async () => {
    if (!session?.id) return;
    try {
      await seenChatHistory(session?.id);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    if (session?.id) {
      fetchChatMessages(1, { reset: true });
    }
  }, [session?.id]);

  useEffect(() => {
    if (didInitialAutoScrollRef.current) return;
    if (pageNumber !== 1) return;
    if (!messages || messages.length === 0) return;

    didInitialAutoScrollRef.current = true;

    const scroll = () => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(scroll);
    } else {
      setTimeout(scroll, 50);
    }
  }, [pageNumber, messages?.length]);

  useLayoutEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const pageSize = constants?.pageSize || 10;
    const hasOnlyFirstPage = (totalMessageCount || messages.length) <= pageSize;
    if (pageNumber === 1 && hasOnlyFirstPage) {
      const remaining = Math.max(0, el.clientHeight - el.scrollHeight);
      setTopSpacerHeight(remaining);
    } else {
      setTopSpacerHeight(0);
    }
  }, [messages, pageNumber, totalMessageCount]);

  const fetchChatMessages = async (
    targetPageNo,
    { reset } = { reset: false },
  ) => {
    if (!session?.id) return;

    try {
      if (reset) setIsLoading(true);
      if (reset) setCanHandleScroll(false);
      setError(null);

      const params = {
        pageNo: targetPageNo || 1,
        pageSize: 10,
        AiChatSessionId: session.id,
      };

      const response = await getSessionChat(params);

      const mappedMessages = (response?.items || [])
        .map((msg) => ({
          id: msg?.id,
          text: msg?.message || "",
          sender: msg?.sender || "Ai",
          createdAt: msg?.createdAt,
          ratings: msg?.ratings,
          attachments: msg?.attachments || [],
          filePaths: msg?.filePaths || [],
        }))
        .sort(
          (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
        );

      if (reset || targetPageNo === 1) {
        setMessages(mappedMessages);
        setPageNumber(1);
      } else {
        setMessages((prev) => [...mappedMessages, ...prev]);
      }

      setTotalMessageCount(response?.totalCount || 0);
      setPageNumber(targetPageNo || 1);
    } catch (err) {
      setError(err?.message);
      handleError(err);
    } finally {
      if (reset) setIsLoading(false);
      setTimeout(() => setCanHandleScroll(true), 300);
    }
  };

  const handleScroll = (e) => {
    if (!canHandleScroll) return;
    const container = e.target;
    const isAtTop = container.scrollTop < 100;

    if (
      isAtTop &&
      !isLoading &&
      !isFetchingOlder &&
      messages.length < totalMessageCount
    ) {
      setIsFetchingOlder(true);
      setCanHandleScroll(false);
      lastScrollHeightRef.current = container.scrollHeight;

      const nextPage = pageNumber + 1;
      fetchChatMessages(nextPage, { reset: false })
        .catch(() => {})
        .finally(() => {
          setIsFetchingOlder(false);
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - lastScrollHeightRef.current;
            container.scrollTop = heightDiff;
            setCanHandleScroll(true);
          }, 100);
        });
    }
  };

  const renderHeader = () => (
    <div className="content_wrapper_pms">
      <div>
        <h2 className="title pms_heading page_title_margin_top page_title_margin_bottom">
          {t("aiAssistant.chatHistory")}
        </h2>
        <Text size="14px" weight="300" color="#000000">
          {t("aiAssistant.aiAssistSubheading")}
        </Text>
      </div>
      <Button onClick={() => handleExport(session)}>{t("export")}</Button>
    </div>
  );

  const renderInfoRow = (label, value) => (
    <div className={chatStyles["chat-info-row"]}>
      <div className={chatStyles["chat-info-label"]}>{label}</div>
      <div className={chatStyles["chat-info-value"]}>{value || "--"}</div>
    </div>
  );

  const renderDetails = () => (
    <div className={chatStyles["chat-info-panel"]}>
      {renderInfoRow(
        t("aiAssistant.ratingReviews.username"),
        session?.userFullName,
      )}
      {renderInfoRow(
        t("aiAssistant.ratingReviews.emailAddresss"),
        session?.userEmailId,
      )}
      {renderInfoRow(
        t("aiAssistant.ratingReviews.accountOwner"),
        session?.accountOwnerName,
      )}
      {renderInfoRow(
        t("aiAssistant.ratingReviews.date"),
        session?.createdAt
          ? formatLocalDateTime(session.createdAt, "MMM DD, YYYY")
          : "--",
      )}
      {renderInfoRow(t("aiAssistant.ratingReviews.threadName"), session?.title)}
    </div>
  );

  const renderChats = () => (
    <div className={chatStyles["chat-conversation-panel"]}>
      <div className={chatStyles["chat-conversation-title"]}>Chats</div>
      <div
        className={chatStyles["chat-messages-container"]}
        ref={chatScrollRef}
        onScroll={handleScroll}
      >
        {topSpacerHeight > 0 && <div style={{ height: topSpacerHeight }} />}
        {isLoading && <Loader />}
        {isFetchingOlder && (
          <div className="text-placeholder-200 shimmer-animation"></div>
        )}

        {error && (
          <div className={chatStyles["error-message"]}>
            <Text size="14px" weight="400" color="#EE4F4F">
              {error}
            </Text>
          </div>
        )}

        {!isLoading && !error && messages.length === 0 && (
          <Empty Message={t("aiAssistant.noChats")} />
        )}

        {messages.map((msg, index) => (
          <div
            key={msg?.id || `msg-${index}`}
            className={chatStyles["chat-message"]}
          >
            <div
              className={
                msg.sender === "User"
                  ? chatStyles["chat-message-user"]
                  : chatStyles["chat-message-ai"]
              }
            >
              <div
                className={`${chatStyles["chat-message-bubble"]} ${
                  msg.sender === "User"
                    ? chatStyles["chat-message-bubble-user"]
                    : chatStyles["chat-message-bubble-ai"]
                }`}
              >
                {/* Show attachments for user messages */}
                {msg.sender === "User" &&
                  ((msg.attachments && msg.attachments.length > 0) ||
                    (msg.filePaths && msg.filePaths.length > 0)) && (
                    <div className="message-attachments">
                      {/* Render attachments if available */}
                      {msg.attachments &&
                        msg.attachments.length > 0 &&
                        msg.attachments.map((attachment, attIndex) => (
                          <div
                            key={attIndex}
                            className="message-attachment-item"
                          >
                            {attachment.type?.startsWith("image/") ? (
                              <img
                                onClick={() => {
                                  setFileViewer(true);
                                  setFileMessage(attachment);
                                }}
                                src={attachment.blobUrl || attachment.preview}
                                alt={attachment.name}
                                className="message-attachment-image cursor-pointer"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="message-attachment-document">
                                <div className="download-icon">⬇</div>
                                <span className="file-name">
                                  {attachment.name}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* Render filePaths if available */}
                      {msg.filePaths &&
                        msg.filePaths.length > 0 &&
                        msg.filePaths.map((filePath, pathIndex) => {
                          const isImage =
                            /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filePath);
                          const fileName = filePath.split("/").pop() || "file";

                          return (
                            <div
                              key={`filepath-${pathIndex}`}
                              className="message-attachment-item"
                            >
                              {isImage ? (
                                <img
                                  onClick={() => {
                                    setFileViewer(true);
                                    setFileMessage({
                                      blobUrl: filePath,
                                      preview: filePath,
                                      name: fileName,
                                    });
                                  }}
                                  src={filePath}
                                  alt={fileName}
                                  className="message-attachment-image cursor-pointer"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="message-attachment-document">
                                  <div className="download-icon">⬇</div>
                                  <span className="file-name">{fileName}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                {/* Show message text if exists */}
                {msg.text && (
                  <Text
                    size="13px"
                    weight="400"
                    color="#2D3245"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {msg.text}
                  </Text>
                )}
              </div>
            </div>

            {msg.sender !== "User" &&
              Array.isArray(msg?.ratings) &&
              msg.ratings.length > 0 && (
                <div className={chatStyles["rating-star-container"]}>
                  <img
                    src={starMarkedIcon}
                    alt="starMarkedIcon"
                    className="cursor-pointer"
                    style={{ marginTop: 4 }}
                    onClick={() => {
                      setModalOpen(true);
                      setSelectedResponse(msg);
                    }}
                  />
                </div>
              )}
          </div>
        ))}

        <div ref={chatEndRef} />
      </div>
    </div>
  );

  return (
    <>
      <Page onBack={() => goBack()} className={styles["global-main-page"]}>
        {showLoader && <Loader />}
        {renderHeader()}
        <Card className={styles.instruction_card + " custom-table"}>
          <div className={chatStyles["chat-details-container"]}>
            {renderDetails()}
            {renderChats()}
          </div>
        </Card>
      </Page>

      {modalOpen && (
        <CustomFeedbackModal
          t={t}
          setModalOpen={setModalOpen}
          modalOpen={modalOpen}
          selectedResponse={selectedResponse}
          chatSessionId={session?.id}
          onRatingSuccess={() => {}}
        />
      )}

      {fileViewer && fileMessage && (
        <ImageViewer
          imageUrl={fileMessage?.blobUrl || fileMessage?.preview}
          isOpen={fileViewer}
          closeImageViewerModal={() => setFileViewer(false)}
          hideDownloadIcon={true}
        />
      )}
    </>
  );
}

export default withTranslation()(ViewChatDetails);
